import { useApolloClient } from '@apollo/client';
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  Switch,
  Tooltip
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { setErrorSnackbarMessage } from '../helpers/ErrorHelper';
import { AdminUpdateTableMutationDocument } from '../services/graphql/typed-operations';
import { addSnackbarMessages, snackbarMessagesState } from '../store/app';
import { hasPosSelector, tablesState, updateState } from '../store/gastronomy';

interface Props {
  table: TableFragment;
}

const TableStatus: React.FC<Props> = ({ table }) => {
  const [status, setStatus] = useState(
    table.status === 'ACTIVE' ? true : false
  );
  const [statusOverride, setStatusOverride] = useState(
    table.status === 'INACTIVE'
      ? false
      : table.status_override === 'INACTIVE'
      ? false
      : true
  );
  const hasPos = useRecoilValue(hasPosSelector);
  const setSnackbarMessages = useSetRecoilState(snackbarMessagesState);
  const setTables = useSetRecoilState(tablesState);
  const apolloClient = useApolloClient();

  useEffect(() => {
    setStatus(table.status === 'ACTIVE' ? true : false);
    setStatusOverride(
      table.status === 'INACTIVE'
        ? false
        : table.status_override === 'INACTIVE'
        ? false
        : true
    );
  }, [table]);

  const update = async ({
    newStatus,
    newStatusOverride
  }: {
    newStatus?: boolean;
    newStatusOverride?: boolean;
  }) => {
    try {
      const input: AdminUpdateTableMutationVariables['input'] = {
        uuid: table.uuid
      };

      if (hasPos) {
        input.status_override = newStatusOverride ? 'ACTIVE' : 'INACTIVE';
      } else {
        input.status = newStatus ? 'ACTIVE' : 'INACTIVE';
      }

      const { data } = await apolloClient.mutate({
        mutation: AdminUpdateTableMutationDocument,
        variables: { input }
      });

      if (data) {
        const newTable = data.adminUpdateTable;

        updateState(newTable, setTables);

        addSnackbarMessages(
          [
            {
              type: 'success',
              message: `Status erfolgreich aktualisiert`
            }
          ],
          setSnackbarMessages
        );
      }
    } catch (error) {
      setErrorSnackbarMessage(error, setSnackbarMessages);
    }
  };

  const toggleStatus = async () => {
    const newStatus = !status;

    setStatus(newStatus);

    await update({ newStatus });
  };

  const toggleStatusOverride = async () => {
    const newStatusOverride = !statusOverride;

    setStatusOverride(newStatusOverride);

    await update({ newStatusOverride });
  };

  return (
    <Tooltip title="Gast kann diese Bestellart verwenden">
      <FormControl component="fieldset">
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                disabled={hasPos}
                checked={status}
                onChange={toggleStatus}
                color="primary"
              />
            }
            labelPlacement="start"
            label={hasPos ? 'POS Status' : ''}
          />
          {hasPos && (
            <FormControlLabel
              control={
                <Switch
                  checked={statusOverride}
                  onChange={toggleStatusOverride}
                  color="primary"
                  disabled={!status}
                />
              }
              labelPlacement="start"
              label="Überschreiben"
            />
          )}
        </FormGroup>
      </FormControl>
    </Tooltip>
  );
};

export default TableStatus;
